/**
 * Lead job cockpit helpers (Sprint 8.8 Phase 2).
 * Pure extractors for summary / pricing / ops / confidence / journey.
 *
 * Prefer automated provisional quote + pricingExplanation over legacy
 * pricingSuggestion (truck-led / corridor engines).
 */

import {
  formatLeadQuoteValue,
  getLeadEngine,
  getLeadOperationalBucket,
  getLeadOperationalLabel,
  getLeadProviderStatus,
  getLeadQuoteValue,
  getLeadTruckRecommendation,
} from "./leadOperationalStatus.js";

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {Object|null}
 */
function resolveLinkedQuote(lead, quote = null) {
  return quote || lead?.__linkedQuote || lead?.linkedQuote || null;
}

/**
 * @param {Object|null} quote
 * @returns {Object|null}
 */
function getPricingExplanation(quote) {
  return quote?.automatedPreparation?.pricingExplanation || null;
}

/**
 * @param {Object} lead
 * @returns {string}
 */
export function getLeadRouteLabel(lead) {
  const from = lead?.moving_from || lead?.from || lead?.pickupAddress?.formattedAddress;
  const to = lead?.moving_to || lead?.to || lead?.deliveryAddress?.formattedAddress;
  if (from && to) return `${from} → ${to}`;
  if (from) return `${from} → ?`;
  if (to) return `? → ${to}`;
  return "Route pending";
}

/**
 * @param {string|null|undefined} size
 * @returns {string}
 */
export function formatTruckLabel(size) {
  if (!size) return "—";
  const raw = String(size);
  if (/ton/i.test(raw) && !/_/.test(raw)) return raw;
  return raw
    .replace(/_/g, " ")
    .replace(/\bton\b/i, "Ton")
    .replace(/\b(\d+)\s*ton\b/i, "$1 Ton");
}

/** Seed-aligned fallback when customerLabel is not on the lead yet. */
const DEFAULT_TRUCK_LOAD_LABELS = Object.freeze({
  bakkie: "Mini Move",
  "1_ton": "Small Move",
  "4_ton": "Medium Move",
  "8_ton": "Large Move",
  "16_ton": "XL Move",
});

/**
 * Truck size with load band in brackets, e.g. "1 Ton (Small Move)".
 * @param {string|null|undefined} size
 * @param {string|null|undefined} [customerLabel]
 * @returns {string}
 */
export function formatTruckLabelWithLoad(size, customerLabel = null) {
  const truck = formatTruckLabel(size);
  if (truck === "—") return "—";
  const load =
    (customerLabel && String(customerLabel).trim()) ||
    DEFAULT_TRUCK_LOAD_LABELS[String(size || "").toLowerCase()] ||
    null;
  return load ? `${truck} (${load})` : truck;
}

/**
 * Corridor badge label for list/ops (e.g. "WC-GP").
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {string|null}
 */
export function getLeadCorridorBadge(lead, quote = null) {
  const linked = resolveLinkedQuote(lead, quote);
  const expl = getPricingExplanation(linked);
  const fromAuto = lead?.automatedPricing?.corridorId;
  const raw =
    fromAuto ||
    expl?.corridor?.id ||
    expl?.corridorId ||
    null;
  if (!raw) return null;
  const id = String(raw).replace(/\s+/g, "").toUpperCase();
  // Same-province pseudo-corridors (GP-GP / WC-WC) are not useful list badges.
  const parts = id.split("-");
  if (parts.length === 2 && parts[0] && parts[0] === parts[1]) return null;
  return id;
}

/**
 * Lightweight priority label until Phase 4 score exists.
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {"High"|"Medium"|"Low"}
 */
export function getLeadPriorityLabel(lead, quote = null) {
  const withQuote = attachQuote(lead, quote);
  const bucket = getLeadOperationalBucket(withQuote);
  if (bucket === "needs_review") return "High";
  const value = getLeadQuoteValue(withQuote);
  if (value != null && value >= 5000) return "High";
  if (bucket === "ready_to_quote" || bucket === "awaiting_provider") return "High";
  if (bucket === "awaiting_customer" || bucket === "quote_sent") return "Medium";
  if (bucket === "booked") return "Low";
  return "Medium";
}

/**
 * Prefer qualification route km over legacy capped billableDistanceKm.
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {number|null} km
 */
export function getLeadDistanceKm(lead, quote = null) {
  const linked = resolveLinkedQuote(lead, quote);
  const expl = getPricingExplanation(linked);
  // Engine explanation uses routeKm; some older payloads used distanceKm.
  const explKm = Number(expl?.routeKm ?? expl?.distanceKm);
  if (Number.isFinite(explKm) && explKm >= 0) return explKm;

  const fromAuto = Number(
    lead?.automatedPricing?.routeKm ?? lead?.automatedPricing?.distanceKm
  );
  if (Number.isFinite(fromAuto) && fromAuto >= 0) return fromAuto;

  const km = Number(lead?.qualification?.distance?.km);
  if (Number.isFinite(km) && km >= 0) return km;

  const factors = lead?.pricingSuggestion?.calculatedSuggestion?.calculationFactors;
  const billable = Number(factors?.billableDistanceKm);
  if (Number.isFinite(billable) && billable >= 0) return billable;
  return null;
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {number|null}
 */
export function getLeadBillableVolumeM3(lead, quote = null) {
  const linked = resolveLinkedQuote(lead, quote);
  const expl = getPricingExplanation(linked);
  const fromExpl = Number(expl?.billableVolumeM3);
  if (Number.isFinite(fromExpl) && fromExpl > 0) return fromExpl;

  const fromSnapshot = Number(
    linked?.automatedPreparation?.pricingSnapshot?.billableVolumeM3
  );
  if (Number.isFinite(fromSnapshot) && fromSnapshot > 0) return fromSnapshot;

  const fromAuto = Number(lead?.automatedPricing?.billableVolumeM3);
  if (Number.isFinite(fromAuto) && fromAuto > 0) return fromAuto;

  const v = Number(
    lead?.pricingSuggestion?.calculatedSuggestion?.billableVolumeM3 ??
      lead?.qualification?.volume?.m3 ??
      lead?.billableVolumeM3
  );
  return Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {string}
 */
export function getLeadCorridorLabel(lead, quote = null) {
  const linked = resolveLinkedQuote(lead, quote);
  const expl = getPricingExplanation(linked);
  const corridorId = expl?.corridor?.id || expl?.corridorId;
  if (corridorId) return String(corridorId).replace(/-/g, " → ").toUpperCase();

  const origin =
    lead?.pickupAddress?.addressComponents?.province ||
    lead?.qualification?.corridorClass?.origin ||
    null;
  const dest =
    lead?.deliveryAddress?.addressComponents?.province ||
    lead?.qualification?.corridorClass?.destination ||
    null;
  if (origin && dest) {
    const a = String(origin).trim().toUpperCase();
    const b = String(dest).trim().toUpperCase();
    if (a === b) return `${a} (intra)`;
    return `${a} → ${b}`;
  }
  const engine = getLeadEngine(lead, linked);
  if (engine.key === "local") return "Local";
  if (engine.key === "shared_load") return "Interprovince";
  return "—";
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} quote
 * @returns {Object}
 */
function attachQuote(lead, quote) {
  const linked = resolveLinkedQuote(lead, quote);
  if (!linked) return lead || {};
  if (lead?.__linkedQuote === linked || lead?.linkedQuote === linked) {
    return lead;
  }
  return { ...(lead || {}), __linkedQuote: linked };
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 */
export function getLeadJobSummary(lead, quote = null) {
  const withQuote = attachQuote(lead, quote);
  const price = getLeadQuoteValue(withQuote);
  const volumeM3 = getLeadBillableVolumeM3(withQuote);
  const bucket = getLeadOperationalBucket(withQuote);
  return {
    routeLabel: getLeadRouteLabel(withQuote),
    engine: getLeadEngine(withQuote),
    price,
    priceLabel: formatLeadQuoteValue(price),
    truck: formatTruckLabel(getLeadTruckRecommendation(withQuote)),
    volumeM3,
    volumeLabel: volumeM3 != null ? `${volumeM3.toFixed(2)} m³` : "—",
    statusBucket: bucket,
    statusLabel: getLeadOperationalLabel(bucket),
    priority: getLeadPriorityLabel(withQuote),
    provider: getLeadProviderStatus(withQuote),
    reference: withQuote?.referenceNumber || null,
  };
}

/**
 * Build pricing lines from automated quote when available.
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 */
export function getLeadPricingCardModel(lead, quote = null) {
  const withQuote = attachQuote(lead, quote);
  const linked = resolveLinkedQuote(withQuote);
  const expl = getPricingExplanation(linked);
  const total = getLeadQuoteValue(withQuote);
  const volumeM3 = getLeadBillableVolumeM3(withQuote);
  const lines = [];

  if (linked) {
    const snapshotLines =
      linked?.automatedPreparation?.pricingSnapshot?.lineItems || [];
    if (Array.isArray(snapshotLines) && snapshotLines.length > 0) {
      snapshotLines.forEach((line, index) => {
        const amount = Number(line.amount ?? line.total ?? line.value);
        if (!Number.isFinite(amount) || amount <= 0) return;
        lines.push({
          key: String(line.ruleKey || line.key || `line-${index}`),
          label: String(line.label || line.description || "Line"),
          amount,
          amountLabel: formatLeadQuoteValue(amount),
        });
      });
    } else if (Array.isArray(linked.items) && linked.items.length > 0) {
      linked.items.forEach((item, index) => {
        const amount = Number(item.amount ?? item.total ?? item.price);
        if (!Number.isFinite(amount) || amount <= 0) return;
        lines.push({
          key: `item-${index}`,
          label: String(item.description || item.name || "Transport"),
          amount,
          amountLabel: formatLeadQuoteValue(amount),
        });
      });
    } else if (total != null) {
      lines.push({
        key: "transport",
        label: expl?.corridor ? "Corridor transport" : "Transport",
        amount: total,
        amountLabel: formatLeadQuoteValue(total),
      });
    }

    const access = Number(expl?.accessCharges ?? expl?.accessibilityFee);
    if (Number.isFinite(access) && access > 0 && !lines.some((l) => /access/i.test(l.key))) {
      lines.push({
        key: "access",
        label: "Access",
        amount: access,
        amountLabel: formatLeadQuoteValue(access),
      });
    }
  } else {
    const factors =
      lead?.pricingSuggestion?.calculatedSuggestion?.calculationFactors || {};
    const transport =
      Number(factors.baseDistance) + Number(factors.truckSizeBasePrice || 0);
    const access = Number(factors.accessibilityFee);
    const addOns = Number(
      factors.addOnsCost ??
        lead?.pricingSuggestion?.calculatedSuggestion?.addOnsCost ??
        0
    );

    if (Number.isFinite(transport) && transport > 0) {
      lines.push({
        key: "transport",
        label: "Transport",
        amount: transport,
        amountLabel: formatLeadQuoteValue(transport),
      });
    }
    if (Number.isFinite(access) && access > 0) {
      lines.push({
        key: "access",
        label: "Access",
        amount: access,
        amountLabel: formatLeadQuoteValue(access),
      });
    }
    if (Number.isFinite(addOns) && addOns > 0) {
      lines.push({
        key: "addons",
        label: "Add-ons",
        amount: addOns,
        amountLabel: formatLeadQuoteValue(addOns),
      });
    }
  }

  let rateLabel = "—";
  const ratePerM3 = Number(expl?.ratePerM3 ?? expl?.corridor?.ratePerM3);
  if (Number.isFinite(ratePerM3) && ratePerM3 > 0) {
    rateLabel = `${formatLeadQuoteValue(ratePerM3)}/m³`;
  } else if (volumeM3 != null && total != null && volumeM3 > 0) {
    rateLabel = `${formatLeadQuoteValue(total / volumeM3)}/m³`;
  }

  return {
    engine: getLeadEngine(withQuote),
    corridor: getLeadCorridorLabel(withQuote),
    billableVolumeLabel:
      volumeM3 != null ? `${volumeM3.toFixed(2)} m³` : "—",
    rateLabel,
    lines,
    total,
    totalLabel: formatLeadQuoteValue(total),
  };
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 */
export function getLeadOpsPanelRows(lead, quote = null) {
  const withQuote = attachQuote(lead, quote);
  const distanceKm = getLeadDistanceKm(withQuote);
  const accessibility = withQuote?.qualification?.accessibility || {};
  const items = withQuote?.qualification?.items || {};
  const special = [];
  if (items.hasFragileItems) special.push("Fragile");
  if (items.needsBoxes) special.push("Needs boxes");
  if (items.needsBubbleWrap) special.push("Bubble wrap");
  if (items.needsProtectiveBlankets) special.push("Protective blankets");

  const accessBits = [];
  if (accessibility.pickupStairs) accessBits.push("Pickup stairs");
  if (accessibility.dropoffStairs) accessBits.push("Dropoff stairs");
  if (accessibility.estateAccess) accessBits.push("Estate access");
  if (accessibility.accessRestrictions) {
    accessBits.push(String(accessibility.accessRestrictions));
  }

  return [
    {
      key: "truck",
      label: "Truck",
      value: formatTruckLabel(getLeadTruckRecommendation(withQuote)),
    },
    {
      key: "distance",
      label: "Distance",
      value: distanceKm != null ? `${Math.round(distanceKm)} km` : "—",
    },
    {
      key: "collection",
      label: "Collection",
      value: withQuote?.move_date || withQuote?.date ? "Scheduled" : "Pending",
    },
    {
      key: "delivery",
      label: "Delivery",
      value: withQuote?.delivery_date ? "Scheduled" : "Pending",
    },
    {
      key: "access",
      label: "Access",
      value: accessBits.length ? accessBits.join(", ") : "None noted",
    },
    {
      key: "special",
      label: "Special handling",
      value: special.length ? special.join(", ") : "None",
    },
    {
      key: "provider",
      label: "Provider",
      value: getLeadProviderStatus(withQuote).label,
    },
  ];
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 */
export function getLeadCustomerConfidence(lead, quote = null) {
  const withQuote = attachQuote(lead, quote);
  const linked = resolveLinkedQuote(withQuote);
  const completeness = withQuote?.qualification?.completeness || {};
  const missing = (completeness.missingFields || []).map((f) =>
    String(f).toLowerCase()
  );
  const hasItems =
    (Array.isArray(withQuote?.items) && withQuote.items.length > 0) ||
    completeness.hasAllRequiredFields === true;
  const inventoryBlocked = missing.some(
    (f) => f.includes("item") || f.includes("inventory") || f.includes("photo")
  );
  const from =
    withQuote?.moving_from ||
    withQuote?.from ||
    withQuote?.pickupAddress?.formattedAddress;
  const to =
    withQuote?.moving_to ||
    withQuote?.to ||
    withQuote?.deliveryAddress?.formattedAddress;
  const phone = withQuote?.phone || withQuote?.contactInfo?.phone;

  const explConfidence = getPricingExplanation(linked)?.confidence;
  const confidenceRaw =
    explConfidence ?? withQuote?.pricingSuggestion?.recommendation?.confidence;
  let quoteConfidencePct = null;
  let quoteConfidenceLabel = "—";
  if (typeof confidenceRaw === "number" && Number.isFinite(confidenceRaw)) {
    quoteConfidencePct = Math.round(
      confidenceRaw <= 1 ? confidenceRaw * 100 : confidenceRaw
    );
    quoteConfidenceLabel = `${quoteConfidencePct}%`;
  } else if (typeof confidenceRaw === "string") {
    const lower = confidenceRaw.toLowerCase();
    quoteConfidenceLabel = confidenceRaw;
    if (lower === "high") quoteConfidencePct = 90;
    else if (lower === "medium") quoteConfidencePct = 70;
    else if (lower === "low") quoteConfidencePct = 40;
    if (quoteConfidencePct != null) {
      quoteConfidenceLabel = `${quoteConfidencePct}%`;
    }
  }

  return {
    inventoryConfirmed: hasItems && !inventoryBlocked,
    addressConfirmed: Boolean(from && to),
    phoneVerified: Boolean(phone),
    quoteConfidenceLabel,
    quoteConfidencePct,
  };
}

/**
 * Operational journey (not processing-activity log).
 * @param {Object} lead
 * @param {{ quoteId?: string|null, invoiceId?: string|null, orderId?: string|null }} [links]
 * @param {Object|null|undefined} [quote]
 */
export function getLeadJobJourneySteps(lead, links = {}, quote = null) {
  const withQuote = attachQuote(lead, quote);
  const bucket = getLeadOperationalBucket(withQuote);
  const hasCategory = Boolean(withQuote?.qualification?.category);
  const hasRoute =
    getLeadDistanceKm(withQuote) != null ||
    Boolean(
      (withQuote?.moving_from || withQuote?.from) &&
        (withQuote?.moving_to || withQuote?.to)
    );
  const hasInventory =
    Array.isArray(withQuote?.items) && withQuote.items.length > 0;
  const hasPrice = getLeadQuoteValue(withQuote) != null;
  // Customer-facing "Quote Sent" — not provisional draft existence.
  const linked = resolveLinkedQuote(withQuote);
  const quoteStatus = String(linked?.status || "").toLowerCase();
  const quoteSentToCustomer =
    bucket === "quote_sent" ||
    bucket === "booked" ||
    ["send_queued", "sent", "negotiating", "approved", "accepted", "pending_payment", "deposit_paid", "deposit_verified", "ordered"].includes(
      quoteStatus
    );
  const invoiceId = links.invoiceId;
  const orderId = links.orderId;

  const doneFlags = [
    Boolean(withQuote),
    hasCategory,
    hasRoute,
    hasInventory,
    hasPrice,
    bucket === "booked" ||
      bucket === "quote_sent" ||
      withQuote?.bidding?.status === "closed",
    quoteSentToCustomer,
    bucket === "booked",
    Boolean(invoiceId),
    Boolean(orderId),
  ];

  const labels = [
    { key: "created", label: "Lead Created" },
    { key: "categorised", label: "Auto Categorised" },
    { key: "route", label: "Route Calculated" },
    { key: "volume", label: "Inventory Listed" },
    { key: "price", label: "Price Generated" },
    { key: "provider", label: "Waiting Provider" },
    { key: "quote", label: "Quote Sent" },
    { key: "accepted", label: "Accepted" },
    { key: "invoice", label: "Invoice" },
    { key: "order", label: "Order" },
  ];

  let currentIndex = doneFlags.findIndex((done) => !done);
  if (currentIndex < 0) currentIndex = labels.length - 1;

  if (bucket === "awaiting_provider" && !doneFlags[5]) {
    currentIndex = 5;
  }

  return labels.map((step, index) => {
    if (doneFlags[index]) {
      return { ...step, state: "done" };
    }
    if (index === currentIndex) {
      return { ...step, state: "current" };
    }
    return { ...step, state: "upcoming" };
  });
}
